import {
    computed,
    defineComponent,
    onBeforeUnmount,
    onMounted,
    PropType,
    Ref,
    ref,
    watch,
} from "vue";
import ImageFallback from "../../components/imageFallback.js";
import type {
    LogChannel,
    LogEntry,
    LogUser,
} from "../../core/types.js";
import { useLocale } from "../../locales/i18n.js";
import {
    getRolePrimaryColorCss,
    roleColorGradientToCss,
} from "../../shared/roles/roleColors.js";
import type { RoleColors } from "../../shared/roles/roleColors.js";
import {
    createDiscordMarkdownParser,
    parseDiscordMarkdown,
} from "../../utils/markdown.js";
import {
    loadMemberRoleContext,
    MemberRoleContextResult,
    MemberRoleReference,
} from "./memberRole/memberRoleContext.js";
import MessageLogContent from "./message/MessageLogContent.js";
import { parseMessageLogs } from "./message/messageParser.js";
import {
    DiscordPermissionFlags,
    parsePermissionBits,
} from "./role/permissions.js";
import type { DiscordPermissionFlag } from "./role/permissions.js";

type MemberMap = Ref<Map<string, unknown>>;
type ChannelMap = Ref<Map<string, unknown>>;

type RoleSnapshot = {
    id: string;
    name?: string;
    color?: number;
    colors?: RoleColors | null;
    icon?: string | null;
    iconURL?: string | null;
    unicodeEmoji?: string | null;
    unciodeEmoji?: string | null;
    position?: number;
    permissions?: string | number | bigint | null;
    managed?: boolean;
    mentionable?: boolean;
    hoist?: boolean;
    [key: string]: unknown;
};

type ResolvedMemberRole = {
    id: string;
    role: RoleSnapshot;
    exists: boolean;
    permissions: bigint | null;
    sensitivePermissions: DiscordPermissionFlag[];
};

type RoleStyle = Record<"--role-color" | "--role-dot-background", string>;
type MemberRoleConfidence = NonNullable<MemberRoleContextResult["command"]>["confidence"];

const EmptyMemberRoleContext: MemberRoleContextResult = {
    messages : [],
    members  : [],
    channels : [],
    command  : null,
};

let rolesCache: Promise<Map<string, RoleSnapshot>> | null = null;

function createMemberMap(members: readonly LogUser[], fallback: Map<string, unknown>): Map<string, LogUser> {
    const result = new Map<string, LogUser>();

    for (const [ id, member ] of fallback.entries())
        if (member)
            result.set(id, member as LogUser);

    for (const member of members)
        if (member?.id)
            result.set(member.id, member);

    return result;
}

function createChannelMap(channels: readonly LogChannel[], fallback: Map<string, unknown>): Map<string, LogChannel> {
    const result = new Map<string, LogChannel>();

    for (const [ id, channel ] of fallback.entries())
        if (channel)
            result.set(id, channel as LogChannel);

    for (const channel of channels)
        if (channel?.id)
            result.set(channel.id, channel);

    return result;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function readString(value: unknown): string | undefined {
    return typeof value === "string" && value.length ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function readBoolean(value: unknown): boolean | undefined {
    return typeof value === "boolean" ? value : undefined;
}

function readRoleId(value: unknown): string | null {
    if (typeof value === "string" && value)
        return value;

    if (isRecord(value) && typeof value.id === "string" && value.id)
        return value.id;

    return null;
}

function normalizeRoleSnapshot(value: unknown): RoleSnapshot | null {
    const id = readRoleId(value);

    if (!id)
        return null;

    if (!isRecord(value))
        return { id };

    return {
        id,
        name         : readString(value.name),
        color        : readNumber(value.color),
        colors       : isRecord(value.colors) ? value.colors as RoleColors : null,
        icon         : readString(value.icon) ?? null,
        iconURL      : readString(value.iconURL) ?? null,
        unicodeEmoji : readString(value.unicodeEmoji) ?? null,
        unciodeEmoji : readString(value.unciodeEmoji) ?? null,
        position     : readNumber(value.position),
        permissions  : typeof value.permissions === "string" || typeof value.permissions === "number" || typeof value.permissions === "bigint"
            ? value.permissions
            : null,
        managed      : readBoolean(value.managed),
        mentionable  : readBoolean(value.mentionable),
        hoist        : readBoolean(value.hoist),
    };
}

function getLoggedRoles(log: LogEntry): RoleSnapshot[] {
    const values = Array.isArray(log.extra?.roles)
        ? log.extra.roles
        : [];
    const roles = values
        .map(normalizeRoleSnapshot)
        .filter((role): role is RoleSnapshot => Boolean(role));

    if (!roles.length && log.role)
        roles.push({ id: log.role });

    const byId = new Map<string, RoleSnapshot>();

    for (const role of roles)
        if (!byId.has(role.id))
            byId.set(role.id, role);

    return [...byId.values()];
}

function mergeRoleSnapshots(...roles: Array<RoleSnapshot | null | undefined>): RoleSnapshot {
    const result: Record<string, unknown> = {};

    for (const role of roles) {
        if (!role)
            continue;

        for (const [ key, value ] of Object.entries(role)) {
            if (value !== undefined && value !== null && value !== "")
                result[key] = value;
        }
    }

    return result as RoleSnapshot;
}

function getRoleStyle(role: RoleSnapshot): RoleStyle {
    return {
        "--role-color"          : getRolePrimaryColorCss(role),
        "--role-dot-background" : roleColorGradientToCss(role),
    };
}

function getRoleIconUrl(role: RoleSnapshot): string | null {
    const rawIcon = role.iconURL ?? role.icon;

    if (!rawIcon)
        return null;

    if (rawIcon.startsWith("http://") || rawIcon.startsWith("https://"))
        return rawIcon;

    return `https://cdn.discordapp.com/role-icons/${role.id}/${rawIcon}.webp?size=64`;
}

function getRoleEmoji(role: RoleSnapshot): string | null {
    return role.unicodeEmoji ?? role.unciodeEmoji ?? null;
}

function getSensitivePermissions(bits: bigint | null): DiscordPermissionFlag[] {
    if (bits === null)
        return [];

    return DiscordPermissionFlags.flatMap((permission) => {
        const flag = permission as DiscordPermissionFlag;

        return flag.privileged === true && (bits & flag.bit) !== 0n
            ? [ flag ]
            : [];
    });
}

function loadRoles(): Promise<Map<string, RoleSnapshot>> {
    rolesCache ??= fetch("/api/roles")
        .then((response) => response.ok ? response.json() : [])
        .then((roles: unknown) => {
            if (!Array.isArray(roles))
                return new Map<string, RoleSnapshot>();

            return new Map(roles
                .map(normalizeRoleSnapshot)
                .filter((role): role is RoleSnapshot => Boolean(role))
                .map((role) => [ role.id, role ]));
        })
        .catch(() => new Map<string, RoleSnapshot>());

    return rolesCache;
}

function isBot(member: LogUser | undefined): boolean {
    return Boolean(member?.user && "bot" in member.user && member.user.bot === true);
}

function getDisplayName(member: LogUser): string {
    return member.serverNickname
        || member.nickname
        || member.displayName
        || member.user?.globalName
        || member.user?.username
        || member.id;
}

function getMemberAvatarUrl(member: LogUser): string {
    const serverAvatar = typeof member.serverAvatar === "string" ? member.serverAvatar : null;
    const avatar = typeof member.user?.avatar === "string" ? member.user.avatar : null;

    return serverAvatar
        || avatar
        || `https://cdn.discordapp.com/embed/avatars/${(BigInt(member.id) >> 22n) % 6n}.png`;
}

function getTag(member: LogUser): string {
    return member.user?.username || member.id;
}

function formatLogTime(timestamp: number): string {
    return new Date(timestamp).toLocaleString("fr-FR", {
        day    : "2-digit",
        month  : "2-digit",
        year   : "numeric",
        hour   : "2-digit",
        minute : "2-digit",
        second : "2-digit",
    });
}

function getActionLabels(log: LogEntry) {
    if (log.type === "member_role_remove") {
        return {
            actionIcon        : "label_off",
            actionTitle       : "Rôle retiré",
            targetLabel       : "Membre concerné",
            permissionTitle   : "Permissions sensibles retirées avec ce rôle",
            emptyContext      : "Aucun message de commande ou de bot trouvé pour ce retrait de rôle.",
            contextTitle      : "Corrélation du retrait",
        };
    }

    return {
        actionIcon        : "new_label",
        actionTitle       : "Rôle ajouté",
        targetLabel       : "Membre concerné",
        permissionTitle   : "Permissions sensibles accordées par ce rôle",
        emptyContext      : "Aucun message de commande ou de bot trouvé pour cet ajout de rôle.",
        contextTitle      : "Corrélation de l'ajout",
    };
}

function formatActionSummary(log: LogEntry, roleCount: number): string {
    if (!roleCount)
        return "Aucun rôle lisible dans cette log";

    const roleLabel = roleCount > 1 ? "rôles" : "rôle";

    if (log.type === "member_role_remove")
        return `${roleCount} ${roleLabel} ${roleCount > 1 ? "retirés" : "retiré"} du membre`;

    return `${roleCount} ${roleLabel} ${roleCount > 1 ? "ajoutés" : "ajouté"} au membre`;
}

function getContextKindLabel(command: MemberRoleContextResult["command"]): string {
    if (!command)
        return "Aucune corrélation";

    if (command.kind === "command")
        return command.pattern
            ? `Commande ${command.pattern} probable`
            : "Commande probable";

    if (command.kind === "bot_log")
        return "Message généré par le bot";

    return "Message lié au rôle";
}

function getConfidenceLabel(confidence: MemberRoleConfidence): string {
    switch (confidence) {
        case "high":
            return "forte confiance";
        case "medium":
            return "confiance moyenne";
        default:
            return "signal faible";
    }
}

export default defineComponent({
    props: {
        log: {
            type: Object as PropType<LogEntry>,
            required: true,
        },
        members: {
            type: Object as PropType<MemberMap>,
            required: true,
        },
        channels: {
            type: Object as PropType<ChannelMap>,
            required: true,
        },
    },

    setup(props) {
        const { i18n } = useLocale();
        const markdown = createDiscordMarkdownParser();
        const roles = ref(new Map<string, RoleSnapshot>());
        const loading = ref(false);
        const error = ref<string | null>(null);
        const context = ref<MemberRoleContextResult>(EmptyMemberRoleContext);
        const expandedMessages = ref<Set<string>>(new Set());
        let activeController: AbortController | null = null;
        let requestSequence = 0;
        let mounted = true;

        const members = computed(() => createMemberMap(context.value.members, props.members.value));
        const channels = computed(() => createChannelMap(context.value.channels, props.channels.value));
        const actionLabels = computed(() => getActionLabels(props.log));
        const parsedMessages = computed(() => parseMessageLogs(context.value.messages, markdown, parseDiscordMarkdown));
        const auditExecutorIsBot = computed(() => isBot(props.log.executor ? members.value.get(props.log.executor) : undefined));
        const resolvedRoles = computed<ResolvedMemberRole[]>(() => {
            return getLoggedRoles(props.log).map((loggedRole) => {
                const cachedRole = roles.value.get(loggedRole.id) ?? null;
                const role = mergeRoleSnapshots({
                    id   : loggedRole.id,
                    name : loggedRole.name || `Rôle ${loggedRole.id}`,
                }, loggedRole, cachedRole);
                const permissions = parsePermissionBits(role.permissions);

                return {
                    id: loggedRole.id,
                    role,
                    exists: Boolean(cachedRole),
                    permissions,
                    sensitivePermissions: getSensitivePermissions(permissions),
                };
            });
        });
        const roleReferences = computed<MemberRoleReference[]>(() => {
            return resolvedRoles.value.map(({ role }) => ({
                id   : role.id,
                name : role.name,
            }));
        });
        const roleContextKey = computed(() => {
            return roleReferences.value
                .map((role) => `${role.id}:${role.name ?? ""}`)
                .join("|");
        });

        onMounted(async () => {
            const loadedRoles = await loadRoles();

            if (mounted)
                roles.value = loadedRoles;
        });

        onBeforeUnmount(() => {
            mounted = false;
            activeController?.abort();
        });

        async function refreshContext() {
            activeController?.abort();
            activeController = null;
            requestSequence += 1;

            if (!props.log.executor || !auditExecutorIsBot.value) {
                loading.value = false;
                error.value = null;
                context.value = EmptyMemberRoleContext;
                expandedMessages.value = new Set();
                return;
            }

            const sequence = requestSequence;
            const controller = new AbortController();
            activeController = controller;
            loading.value = true;
            error.value = null;

            try {
                const nextContext = await loadMemberRoleContext(props.log, props.log.executor, roleReferences.value, {
                    signal: controller.signal,
                });

                if (sequence === requestSequence) {
                    context.value = nextContext;
                    expandedMessages.value = new Set();
                }
            } catch (cause) {
                if (controller.signal.aborted)
                    return;

                error.value = cause instanceof Error ? cause.message : "Impossible de charger la corrélation.";
                context.value = EmptyMemberRoleContext;
            } finally {
                if (sequence === requestSequence) {
                    loading.value = false;
                    activeController = null;
                }
            }
        }

        watch(
            () => [
                props.log.id ?? `${props.log.type}:${props.log.createdAt}`,
                props.log.executor,
                props.log.target,
                auditExecutorIsBot.value,
                roleContextKey.value,
            ],
            refreshContext,
            { immediate: true },
        );

        function getMember(id: string | null | undefined): LogUser | undefined {
            return id ? members.value.get(id) : undefined;
        }

        function getMessageKey(log: LogEntry): string {
            return `${log.id ?? "no-id"}:${log.type}:${log.createdAt}`;
        }

        function toggleMessageExpansion(log: LogEntry) {
            const key = getMessageKey(log);
            const next = new Set(expandedMessages.value);

            if (next.has(key))
                next.delete(key);
            else
                next.add(key);

            expandedMessages.value = next;
        }

        function renderRoleIcon(role: RoleSnapshot) {
            const iconUrl = getRoleIconUrl(role);
            const emoji = getRoleEmoji(role);

            if (iconUrl)
                return <img class="role-preview-role-icon" src={iconUrl} alt="" />;

            if (emoji)
                return <span class="role-preview-role-emoji">{emoji}</span>;

            return null;
        }

        function renderRoleChip(role: RoleSnapshot) {
            return (
                <div class="role-preview-role-chip" style={getRoleStyle(role)}>
                    <span class="role-preview-role-dot" />
                    {renderRoleIcon(role)}
                    <span class="role-preview-role-name">{role.name || role.id}</span>
                </div>
            );
        }

        function renderUser(id: string | null | undefined, label: string) {
            const member = getMember(id);
            const displayName = member ? getDisplayName(member) : id || "Inconnu";

            return (
                <div class="member-role-preview-user-block">
                    <span>{label}</span>
                    {id
                        ? (
                            <router-link to={`/users/${id}`} class="member-role-preview-user">
                                <ImageFallback
                                    src={member ? getMemberAvatarUrl(member) : undefined}
                                    default="https://cdn.discordapp.com/embed/avatars/0.png"
                                    alt={displayName}
                                />
                                <div>
                                    <strong>{displayName}</strong>
                                    <small>{member ? getTag(member) : id}</small>
                                </div>
                                {isBot(member) && <span class="member-role-preview-user-badge">Bot</span>}
                            </router-link>
                        )
                        : <div class="member-role-preview-user member-role-preview-user-missing">Inconnu</div>}
                </div>
            );
        }

        function renderSummary() {
            const roleCount = resolvedRoles.value.length;

            return (
                <div class="member-role-preview-summary">
                    {renderUser(props.log.target || props.log.executor, actionLabels.value.targetLabel)}

                    <div class="member-role-preview-action">
                        <span class="material-symbols-outlined">{actionLabels.value.actionIcon}</span>
                        <div>
                            <strong>{actionLabels.value.actionTitle}</strong>
                            <small>{formatActionSummary(props.log, roleCount)}</small>
                        </div>
                    </div>

                    {renderUser(props.log.executor, "Exécutant audit")}
                </div>
            );
        }

        function renderSensitivePermission(permission: DiscordPermissionFlag) {
            return (
                <div class="member-role-preview-sensitive-permission">
                    <span class="material-symbols-outlined">warning</span>
                    <div>
                        <strong>{i18n(`permissions.${permission.key}.name`)}</strong>
                        <span>{i18n(`permissions.${permission.key}.description`)}</span>
                    </div>
                    <span class="role-preview-permission-badge">
                        {i18n("role_preview.permissions.privileged")}
                    </span>
                </div>
            );
        }

        function renderRoleDetails(resolvedRole: ResolvedMemberRole) {
            const { exists, permissions, role, sensitivePermissions } = resolvedRole;

            if (!exists && permissions === null) {
                return (
                    <div class="member-role-preview-role-alert">
                        <span class="material-symbols-outlined">info</span>
                        <p>
                            Ce rôle n'existe plus dans les rôles actuels du serveur. La snapshot de cette log ne contient pas assez d'informations pour afficher ses permissions ou ses détails pour l'instant.
                        </p>
                    </div>
                );
            }

            if (permissions === null) {
                return (
                    <p class="role-preview-empty">
                        Permissions indisponibles pour ce rôle.
                    </p>
                );
            }

            if (!sensitivePermissions.length) {
                return (
                    <p class="role-preview-empty">
                        Aucune permission sensible détectée sur ce rôle.
                    </p>
                );
            }

            return (
                <div class="member-role-preview-sensitive-list">
                    <h4>{actionLabels.value.permissionTitle}</h4>
                    {sensitivePermissions.map((permission) => renderSensitivePermission(permission))}
                </div>
            );
        }

        function renderRoleCard(resolvedRole: ResolvedMemberRole) {
            const { exists, role } = resolvedRole;

            return (
                <div class={{
                    "member-role-preview-role-card"         : true,
                    "member-role-preview-role-card-missing" : !exists,
                }} key={role.id}>
                    <div class="member-role-preview-role-header">
                        {renderRoleChip(role)}
                        <div class="role-preview-role-meta">
                            <span>ID {role.id}</span>
                            {typeof role.position === "number" && <span>Position {role.position}</span>}
                            <span>{exists ? "Rôle actuel résolu" : "Rôle supprimé ou indisponible"}</span>
                        </div>
                    </div>

                    {renderRoleDetails(resolvedRole)}
                </div>
            );
        }

        function renderRoles() {
            if (!resolvedRoles.value.length) {
                return (
                    <div class="role-preview-card">
                        <h3>Rôles</h3>
                        <p class="role-preview-empty">Aucun rôle lisible dans cette log.</p>
                    </div>
                );
            }

            return (
                <div class="role-preview-card">
                    <h3>Rôles</h3>
                    <div class="member-role-preview-role-list">
                        {resolvedRoles.value.map(renderRoleCard)}
                    </div>
                </div>
            );
        }

        function renderReason() {
            const reason = props.log.extra?.reason;

            if (typeof reason !== "string" || !reason.trim())
                return null;

            return (
                <div class="role-preview-card">
                    <h3>Raison audit</h3>
                    <p>{reason}</p>
                </div>
            );
        }

        function renderContextSummary() {
            const command = context.value.command;

            if (!command)
                return null;

            const executor = getMember(command.log.executor);
            const displayName = executor ? getDisplayName(executor) : command.log.executor || "Utilisateur inconnu";

            return (
                <div class="member-role-preview-context-summary">
                    <span class="material-symbols-outlined">
                        {command.kind === "bot_log" ? "smart_toy" : "terminal"}
                    </span>
                    <div>
                        <strong>{getContextKindLabel(command)}</strong>
                        <small>
                            {displayName}
                            {" · "}
                            {getConfidenceLabel(command.confidence)}
                            {" · "}
                            {formatLogTime(command.log.createdAt)}
                        </small>
                    </div>
                </div>
            );
        }

        function renderMessage(log: LogEntry) {
            const member = getMember(log.executor);
            const channel = log.channel ? channels.value.get(log.channel) : null;
            const parsed = parsedMessages.value.find((message) => message.id === log.id && message.createdAt === log.createdAt);
            const isCommand = context.value.command?.log === log;
            const isExpanded = isCommand || expandedMessages.value.has(getMessageKey(log));
            const channelLabel = channel
                ? `#${channel.name}`
                : (log.channel ? `#${log.channel}` : "Salon inconnu");

            return (
                <div class={{
                    "member-role-preview-message"         : true,
                    "member-role-preview-message-command" : isCommand,
                }} key={getMessageKey(log)}>
                    <ImageFallback
                        src={member ? getMemberAvatarUrl(member) : undefined}
                        default="https://cdn.discordapp.com/embed/avatars/0.png"
                        alt={member ? getDisplayName(member) : "Utilisateur inconnu"}
                    />
                    <div class="member-role-preview-message-body">
                        <div class="member-role-preview-message-header">
                            <div class="member-role-preview-message-identity">
                                <div class="member-role-preview-message-meta">
                                    <strong>{member ? getDisplayName(member) : log.executor || "Utilisateur inconnu"}</strong>
                                    {isBot(member) && <span>Bot</span>}
                                    {isCommand && <span>Corrélation</span>}
                                    <small>{formatLogTime(log.createdAt)}</small>
                                </div>
                                <div class="member-role-preview-message-channel">
                                    <span class="material-symbols-outlined">tag</span>
                                    <small>{channelLabel}</small>
                                </div>
                            </div>

                            {!isCommand && (
                                <button
                                    type="button"
                                    class="member-role-preview-message-expand"
                                    aria-expanded={isExpanded}
                                    title={isExpanded ? "Masquer le message" : "Afficher le message"}
                                    onClick={() => toggleMessageExpansion(log)}
                                >
                                    <span class="material-symbols-outlined">
                                        {isExpanded ? "expand_less" : "expand_more"}
                                    </span>
                                </button>
                            )}
                        </div>

                        {isExpanded && (
                            <div class="member-role-preview-message-content">
                                {parsed
                                    ? <MessageLogContent log={parsed} />
                                    : "Message vide"}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        function renderContext() {
            if (loading.value)
                return (
                    <div class="member-role-preview-loading">
                        <span class="member-role-preview-loading-spinner" aria-hidden="true" />
                        <span>Chargement de la corrélation...</span>
                    </div>
                );

            if (error.value)
                return <p class="role-preview-empty">{error.value}</p>;

            if (!context.value.messages.length)
                return <p class="role-preview-empty">{actionLabels.value.emptyContext}</p>;

            return (
                <>
                    {renderContextSummary()}
                    <div class="member-role-preview-message-list">
                        {context.value.messages.map(renderMessage)}
                    </div>
                </>
            );
        }

        return () => (
            <div class="role-preview member-role-preview">
                {renderSummary()}
                {renderRoles()}
                {renderReason()}

                {auditExecutorIsBot.value && (
                    <div class="role-preview-card member-role-preview-context-card">
                        <h3>{actionLabels.value.contextTitle}</h3>
                        {renderContext()}
                    </div>
                )}
            </div>
        );
    },
});
